<template>
	<aside class="dn-sidebar" :class="{ 'dn-sidebar--collapsed': collapsed, 'dn-sidebar--mobile-open': mobileOpen }">
		<!-- Logo -->
		<div class="dn-sidebar__logo-row">
			<router-link :to="{ name: 'v2-index' }" class="dn-sidebar__logo">
				<span class="dn-sidebar__logo-icon">
					<Logo />
				</span>
				<span v-if="!collapsed" class="dn-sidebar__logo-text">
					<span class="dn-sidebar__logo-title">{{ platformTitle }}</span>
					<span class="dn-sidebar__logo-sub">{{ platformSubtitle }}</span>
				</span>
			</router-link>
		</div>

		<!-- Toggle button -->
		<button class="dn-sidebar__toggle" @click="$emit('update:collapsed', !collapsed)" :title="collapsed ? 'Expand' : 'Collapse'">
			<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
				<path v-if="!collapsed" d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
				<path v-else d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>
		</button>

		<nav class="dn-sidebar__nav">
			<div v-if="!collapsed" class="dn-sidebar__group-label">Main</div>
			<router-link :to="{ name: 'v2-index' }" class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-index') }">
				<svg width="16" height="16" viewBox="0 0 16 16"><rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.5"/><rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.5"/><rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.5"/><rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" fill="none" stroke-width="1.5"/></svg>
				<span v-if="!collapsed">Metrics</span>
			</router-link>

			<div v-if="!collapsed" class="dn-sidebar__group-label">Advertising</div>
			<div v-else class="dn-sidebar__divider" />
			<router-link :to="{ name: 'v2-campaign-list' }" class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-campaign') }">
				<svg width="16" height="16" viewBox="0 0 16 16"><path d="M6 12.5L12.5 8L6 3.5V12.5Z" stroke="currentColor" fill="none" stroke-width="1.5" stroke-linejoin="round"/></svg>
				<span v-if="!collapsed">{{ $t('campaign.campaigns') | capitalize }}</span>
			</router-link>
			<router-link v-if="!isSSPViewer" :to="{ name: 'v2-request-list' }" class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-request') }">
				<svg width="16" height="16" viewBox="0 0 16 16"><path d="M8 1.5C8 1.5 6 1.5 5.5 4H10.5C10 1.5 8 1.5 8 1.5Z" stroke="currentColor" fill="none" stroke-width="1.3"/><path d="M4 4H12V11.5C12 12.6 11.1 13.5 10 13.5H6C4.9 13.5 4 12.6 4 11.5V4Z" stroke="currentColor" fill="none" stroke-width="1.3"/></svg>
				<span v-if="!collapsed">{{ $t('request_list.requests') | capitalize }}</span>
			</router-link>
			<router-link :to="{ name: 'v2-advertiser-list' }" class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-advertiser') }">
				<svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="5" r="3" stroke="currentColor" fill="none" stroke-width="1.3"/><path d="M2.5 14C2.5 11 5 9 8 9C11 9 13.5 11 13.5 14" stroke="currentColor" fill="none" stroke-width="1.3"/></svg>
				<span v-if="!collapsed">{{ $t('advertiser.all_advertisers') | capitalize }}</span>
			</router-link>

			<!-- SSP-only items -->
			<template v-if="side === 'ssp'">
				<div v-if="!collapsed" class="dn-sidebar__group-label">Network</div>
				<div v-else class="dn-sidebar__divider" />
				<router-link :to="{ name: 'v2-network' }" class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-network') }">
					<svg width="16" height="16" viewBox="0 0 16 16"><circle cx="4" cy="4" r="1.5" stroke="currentColor" fill="none" stroke-width="1.3"/><circle cx="12" cy="4" r="1.5" stroke="currentColor" fill="none" stroke-width="1.3"/><circle cx="4" cy="12" r="1.5" stroke="currentColor" fill="none" stroke-width="1.3"/><circle cx="12" cy="12" r="1.5" stroke="currentColor" fill="none" stroke-width="1.3"/><path d="M8 4H5.5M8 4H10.5M8 12H5.5M8 12H10.5M4 5.5V10.5M12 5.5V10.5" stroke="currentColor" stroke-width="1.3"/></svg>
					<span v-if="!collapsed">Network</span>
				</router-link>
				<router-link :to="{ name: 'v2-agency-list' }" class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-agency') }">
					<svg width="16" height="16" viewBox="0 0 16 16"><rect x="2" y="3" width="12" height="10" rx="1.5" stroke="currentColor" fill="none" stroke-width="1.3"/><path d="M5 7H11M5 10H9" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
					<span v-if="!collapsed">{{ $t('common.agencies') | capitalize }}</span>
				</router-link>
			</template>

			<div v-if="!collapsed" class="dn-sidebar__group-label">Finance</div>
			<div v-else class="dn-sidebar__divider" />
			<router-link :to="{ name: 'v2-billing' }" class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-billing') }">
				<svg width="16" height="16" viewBox="0 0 16 16"><rect x="2" y="4" width="12" height="9" rx="1.5" stroke="currentColor" fill="none" stroke-width="1.3"/><path d="M2 7H14" stroke="currentColor" stroke-width="1.3"/></svg>
				<span v-if="!collapsed">Billing</span>
			</router-link>

			<div v-if="!collapsed" class="dn-sidebar__group-label">System</div>
			<div v-else class="dn-sidebar__divider" />
			<router-link v-if="side === 'ssp'" :to="{ name: 'v2-whitelabel-settings' }" class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-whitelabel') }">
				<svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="8" r="2" stroke="currentColor" fill="none" stroke-width="1.3"/><path d="M8 2V4M8 12V14M2 8H4M12 8H14M3.8 3.8L5.2 5.2M10.8 10.8L12.2 12.2M12.2 3.8L10.8 5.2M5.2 10.8L3.8 12.2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
				<span v-if="!collapsed">White-label</span>
			</router-link>
			<tippy
				:interactive="true"
				:reactive="true"
				placement="right"
				:arrow="false"
				trigger="mouseenter focus"
				class="dn-sidebar__profile-tippy"
			>
				<template v-slot:trigger>
					<div class="dn-sidebar__item" :class="{ 'dn-sidebar__item--active': isActive('v2-profile') }">
						<svg width="16" height="16" viewBox="0 0 16 16"><circle cx="8" cy="5.5" r="2.5" stroke="currentColor" fill="none" stroke-width="1.3"/><path d="M3 13.5C3 11 5.2 9 8 9C10.8 9 13 11 13 13.5" stroke="currentColor" fill="none" stroke-width="1.3"/></svg>
						<span v-if="!collapsed">{{ $t('common.profile') | capitalize }}</span>
					</div>
				</template>

				<div class="dn-profile-popup">
					<div class="dn-profile-popup__header">
						<p class="dn-profile-popup__name">{{ userName }}</p>
						<p class="dn-profile-popup__email">{{ userEmail }}</p>
					</div>

					<div class="dn-profile-popup__section">
						<span class="dn-profile-popup__label">Agency</span>
						
						<div class="dn-profile-popup__agencies">
							<button
								v-for="agency in availableAgenciesList"
								:key="agency.id + agency.role"
								class="dn-profile-popup__agency-btn"
								:class="{ 'dn-profile-popup__agency-btn--current': currentAgency === agency.id }"
								@click="switchAgencyAndRole(agency.id, agency.role)"
							>
								{{ agency.name }}
								<span class="dn-profile-popup__agency-role">({{ $t(`invitation.${agency.role}`) }})</span>
							</button>
						</div>
					</div>

					<div class="dn-profile-popup__actions">
						<button class="dn-profile-popup__btn" @click="$router.push({ name: 'v2-profile' })">
							{{ $t('common.profile') | capitalize }}
						</button>
						<button class="dn-profile-popup__btn dn-profile-popup__btn--classic" @click="switchToClassic">
							Classic UI
						</button>
					</div>
				</div>
			</tippy>
		</nav>

		<div class="dn-sidebar__footer">
			<span v-if="!collapsed" class="dn-sidebar__version">v{{ gitCommit }}</span>
			<button class="dn-sidebar__logout" @click="logout" :title="collapsed ? 'Logout' : ''">
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
					<path d="M6 2H4C3.45 2 3 2.45 3 3V13C3 13.55 3.45 14 4 14H6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
					<path d="M10 11L13 8L10 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
					<path d="M13 8H6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
				</svg>
				<span v-if="!collapsed">{{ $t('common.logout') | capitalize }}</span>
			</button>
		</div>
	</aside>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Logo from '@/ui/logo.vue';

export default {
	name: 'V2Sidebar',

	components: {
		Logo,
	},

	props: {
		collapsed: {
			type: Boolean,
			default: false,
		},
		mobileOpen: {
			type: Boolean,
			default: false,
		},
	},

	mounted() {
		document.body.classList.add('dn-active');
	},

	beforeDestroy() {
		document.body.classList.remove('dn-active');
	},

	computed: {
		...mapState('app', {
			side: 'side',
			currentRole: 'role',
		}),

		...mapState('profile', {
			userEmail: 'email',
			firstName: 'first_name',
			lastName: 'last_name',
			username: 'username',
		}),

		...mapState('agency', {
			agencyName: 'name',
		}),

		...mapState('profile', {
			availableAgencies: 'availableAgencies',
		}),

		...mapGetters('app', ['isSSPViewer']),

		currentAgency() {
			return parseInt(this.$store.state.app.agency);
		},

		gitCommit() {
			return process.env.VUE_APP_CURRENT_GIT_COMMIT || 'dev';
		},

		platformTitle() {
			const ov = this.$uioverrides || {};
			return ov.html_title || ov.name || process.env.VUE_APP_DEFAULT_TITLE || 'DOOH';
		},

		platformSubtitle() {
			const ov = this.$uioverrides || {};
			return ov.html_description || process.env.VUE_APP_DEFAULT_DESCRIPTION || 'Ad Platform';
		},

		userName() {
			let name = [this.firstName, this.lastName].join(' ').trim();
			return name || this.username || this.userEmail;
		},

		availableAgenciesList() {
			let agencies = [];
			for (let agency of (this.availableAgencies || [])) {
				for (let role of agency.roles) {
					agencies.push({ id: agency.id, name: agency.name, role });
				}
			}
			return agencies;
		},
	},

	methods: {
		...mapActions({
			logoutAction: 'logout',
		}),

		/**
		 * Check if the current route matches a v2- prefix pattern.
		 */
		isActive(prefix) {
			const name = this.$route.name || '';
			if (!name.startsWith('v2-')) return false;
			return name === prefix || name.startsWith(prefix + '-');
		},

		logout() {
			this.logoutAction();
		},

		switchAgencyAndRole(agencyId, role) {
			const side = role.substring(0, 3);
			this.$store.commit('app/setAgency', agencyId);
			this.$store.commit('app/setRole', role);
			this.$store.commit('app/setSide', side);
			window.location.href = '/v2';
		},

		switchToClassic() {
			this.$store.commit('uiVersion/setVersion', 1);
			this.$router.push({ name: 'index' }).catch(() => {});
		},
	},
};
</script>


<style lang="sass" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap')
@import '~@/assets/styles/v2-tokens.sass'

// ── Sidebar ──────────────────────────────
.dn-sidebar
	width: $v2-sidebar-width
	min-width: $v2-sidebar-width
	min-height: 100vh
	background: $v2-sidebar-bg
	display: flex
	flex-direction: column
	padding: 12px 0
	position: fixed
	top: 0
	left: 0
	z-index: 100
	transition: width 0.2s ease, min-width 0.2s ease
	overflow: hidden

	&--collapsed
		width: $v2-sidebar-collapsed-width
		min-width: $v2-sidebar-collapsed-width

	@media (max-width: 768px)
		transform: translateX(-100%)
		width: $v2-sidebar-width
		min-width: $v2-sidebar-width

		&--mobile-open
			transform: translateX(0)

.dn-sidebar__logo-row
	padding: 0 12px
	margin-bottom: 8px

.dn-sidebar__toggle
	display: flex
	align-items: center
	justify-content: center
	width: 32px
	height: 32px
	margin: 0 12px 8px auto
	border: none
	background: rgba(255,255,255,0.06)
	border-radius: 8px
	cursor: pointer
	color: rgba(255,255,255,0.4)
	transition: all 0.15s

	.dn-sidebar--collapsed &
		margin: 0 auto 8px

	&:hover
		background: rgba(255,255,255,0.12)
		color: rgba(255,255,255,0.7)

.dn-sidebar__divider
	height: 1px
	background: rgba(255,255,255,0.06)
	margin: 8px 12px

.dn-sidebar__logo
	display: flex
	align-items: center
	gap: 8px
	text-decoration: none

.dn-sidebar__logo-icon
	display: flex
	align-items: center
	justify-content: center
	width: 32px
	height: 32px
	border-radius: 10px
	overflow: hidden
	flex-shrink: 0

	img
		width: 32px
		height: 32px
		object-fit: contain

	::v-deep .nice-icon-2
		--ni-icon-sign: #fff
		width: 24px
		height: 24px

.dn-sidebar__logo-title
	font-size: 15px
	font-weight: 700
	color: #fff
	display: block
	letter-spacing: 0.02em

.dn-sidebar__logo-sub
	font-size: 10px
	color: $v2-sidebar-text
	display: block

.dn-sidebar__nav
	flex: 1

.dn-sidebar__group-label
	font-size: 9px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.15em
	color: rgba(255,255,255,0.25)
	padding: 16px 16px 6px
	white-space: nowrap
	overflow: hidden

	.dn-sidebar--collapsed &
		text-align: center
		padding: 16px 4px 6px
		font-size: 0
		&::first-letter
			font-size: 9px

.dn-sidebar__item
	display: flex
	align-items: center
	gap: 10px
	padding: 8px 16px
	font-size: 12px
	font-weight: 500
	color: $v2-sidebar-text
	text-decoration: none
	transition: all 0.15s
	white-space: nowrap
	overflow: hidden

	svg
		flex-shrink: 0
		opacity: 0.6
		min-width: 16px

	&:hover
		color: rgba(255,255,255,0.85)
		background: rgba(255,255,255,0.05)

		svg
			opacity: 0.85

	&--active
		color: $v2-sidebar-text-active
		background: rgba($v2-teal, 0.1)
		font-weight: 600

		svg
			opacity: 1
			color: $v2-sidebar-text-active

	.dn-sidebar--collapsed &
		justify-content: center
		padding: 10px 0

.dn-sidebar__footer
	padding: 8px 12px
	border-top: 1px solid rgba(255,255,255,0.08)

	.dn-sidebar--collapsed &
		padding: 8px 0

// ── Profile popup ───────────────────────
.dn-sidebar__profile-tippy
	display: block

.dn-profile-popup
	text-align: left
	min-width: 200px

.dn-profile-popup__header
	margin-bottom: 12px

.dn-profile-popup__role
	display: inline-block
	font-size: 9px
	text-transform: uppercase
	letter-spacing: 0.08em
	color: $v2-text-muted
	background: rgba(0,0,0,0.06)
	padding: 2px 8px
	border-radius: 6px
	margin-bottom: 4px

.dn-profile-popup__name
	font-size: 14px
	font-weight: 600
	color: $v2-text
	margin: 4px 0 0

.dn-profile-popup__email
	font-size: 11px
	color: $v2-text-muted
	margin: 2px 0 0

.dn-profile-popup__section
	padding: 8px 0
	border-top: 1px solid $v2-border

.dn-profile-popup__label
	font-size: 9px
	text-transform: uppercase
	letter-spacing: 0.08em
	color: $v2-text-label
	display: block
	margin-bottom: 2px

.dn-profile-popup__agency
	font-size: 12px
	color: $v2-teal
	font-weight: 500
	display: block

.dn-profile-popup__agencies
	display: flex
	flex-direction: column
	gap: 4px
	margin-top: 8px
	max-height: 180px
	overflow-y: auto

.dn-profile-popup__agency-btn
	display: block
	width: 100%
	text-align: left
	padding: 6px 10px
	border: 1px solid $v2-border
	border-radius: 8px
	background: transparent
	font-family: $v2-font
	font-size: 11px
	color: $v2-text-muted
	cursor: pointer
	transition: all 0.15s

	&:hover
		border-color: $v2-teal
		color: $v2-teal

	&--current
		border-color: $v2-teal
		background: rgba($v2-teal, 0.06)
		color: $v2-teal
		font-weight: 600

.dn-profile-popup__agency-role
	opacity: 0.6
	font-size: 10px

.dn-profile-popup__actions
	display: flex
	gap: 8px
	padding-top: 12px
	border-top: 1px solid $v2-border

.dn-profile-popup__btn
	flex: 1
	border: 1px solid $v2-border
	background: transparent
	padding: 6px 12px
	border-radius: 8px
	font-family: $v2-font
	font-size: 11px
	font-weight: 500
	color: $v2-text
	cursor: pointer
	transition: all 0.15s

	&:hover
		border-color: $v2-teal
		color: $v2-teal

	&--classic
		color: $v2-text-muted

		&:hover
			border-color: $v2-text-muted
			color: $v2-text

.dn-sidebar__version
	display: block
	font-size: 10px
	color: rgba(255,255,255,0.2)
	text-align: center
	padding: 0 0 8px
	font-family: monospace

// ── Logout ──────────────────────────────
.dn-sidebar__logout
	display: flex
	align-items: center
	gap: 10px
	width: 100%
	padding: 8px 16px
	border: none
	background: transparent
	color: rgba(255,255,255,0.45)
	font-family: $v2-font
	font-size: 12px
	font-weight: 500
	cursor: pointer
	border-radius: 8px
	transition: all 0.15s
	white-space: nowrap

	svg
		flex-shrink: 0
		min-width: 16px
		opacity: 0.6

	&:hover
		color: $v2-red
		background: rgba($v2-red, 0.08)

		svg
			opacity: 1
			color: $v2-red

	.dn-sidebar--collapsed &
		justify-content: center
		padding: 10px 0
</style>


<style lang="sass">
// Hide global navbar when V2 sidebar is active
body.dn-active .app__header-panel
	display: none !important

body.dn-active .app
	padding-left: 0 !important
</style>
